import Core      from '@/core/Core';
import router    from '@/router';

import OffersDAO from '@/domains/Offers/OffersDAO';

import Offer  from '@/domains/Offers/Offer';
import type Tender from '@/domains/Tenders/Tender';
import type Demand from '@/domains/Demand/Demand';
import ContextMenuItem from "@/lib/components/VContextMenu/ContextMenuItem";
import TabElementGrouped from "@/lib/components/VTabsV2Grouped/TabElementGrouped";
import type RpcDataPagination from "@/core/services/Rpc/RpcDataPagination";
import type RpcDataSort from "@/core/services/Rpc/RpcDataSort";

/** Причины по которым нельзя сделать то или иное действие */
const REASON_UNAVAILABLE = {
	USER_HAS_NO_ACCESS:  'Нет доступа',
	NOT_PUBLISHED: 'Предложение уже снято с публикации',
};

class OffersService {

	/**
	 * Контекст создания офера «по тендеру»: при клике «Создать предложение» со страницы
	 * тендера, перед навигацией на /offers/edit/:categoryId сюда кладётся тендер,
	 * а страница редактирования офера читает его и заполняет дефолты.
	 */
	static tender: Tender | null = null;

	// ============================================================
	//  Создание офера из контекста (навигация + проброс состояния)
	// ============================================================

	static async createOfferByDesignTender(tender: Tender): Promise<void> {
		this.tender = tender;

		await router.push(`/offers/edit/${this.tender.productCategory.id}`);
	}

	static createOfferByDesignDemand(_demand: Demand): void {
		// todo: реализация для потребностей
	}

	static resetTender(): void {
		this.tender = null;
	}

	static resetDemand(): void {
		// todo: реализация для потребностей
	}

	// ============================================================
	//  CRUD
	// ============================================================


	static async getListPaginated(
		params?: Parameters<typeof OffersDAO.getList>[0] & {
			pagination?: RpcDataPagination;
			sort?: RpcDataSort;
		}
	): Promise<{ data: Offer[]; pagination: RpcDataPagination }> {
		const { pagination, sort, ...filters } = params ?? {};
		const response = await OffersDAO.getList(filters, pagination, sort);

		return {
			data: Core.utils.hydrateArray(response.data, Offer.fromServer),
			pagination: response.meta.pagination,
		};
	}

	/**
	 * Загрузка списка отгрузок.
	 * Для использования вне реестра (диалоги, автокомплиты).
	 */
	static async getList(
		params?: Parameters<typeof OffersDAO.getList>[0]
	): Promise<Offer[]> {
		return (await this.getListPaginated(params)).data;
	}

	/**
	 * Получение конкретного офера
	 */
	static async get(id: number, version: number | null = null): Promise<any> {
		const response = await OffersDAO.get(id);

		return Core.utils.hydrate(response.data, Offer.fromServer);
	}

	/**
	 * Создание офера
	 */
	static async add(offer: Offer): Promise<Offer | null> {
		const errors = offer.validate();

		if (errors.length > 0) {
			Core.showNotification.error(errors.join('\n'));
			return null;
		}

		const response = await OffersDAO.add(offer);

		if (response.ok) {
			Core.showNotification.success('Предложение успешно создано');
			return response.data;
		}

		return null;
	}

	/**
	 * Обновление офера
	 */
	static async update(offer: Offer): Promise<any> {
		const errors = offer.validate();

		if (errors.length > 0) {
			Core.showNotification.error(errors.join('\n'));
			return null;
		}

		try {
			const { data } = await OffersDAO.update(offer);

			if (data) {
				Core.showNotification.success('Предложение успешно обновлено');
				return data;
			}

			/** Логируем подозрительный кейс когда update прошёл но данных нет */
			const dateForRate = offer.getDateForCurrencyRate();

			Core.logException({
				packageCargoVolume:             offer.packageCargoVolume,
				packageVolume:                  offer.packageVolume,
				packageCargoDensity:            offer.packageCargoDensity,
				transportationRoutePricePerM3:  offer.transportationRoute?.costPerM3,
				transportationRoutePricePerKg:  offer.transportationRoute?.costPerKg,
			});

			throw new Error('Обновление офера вернуло пустой результат');
		} catch (error) {
			console.warn('Ошибка обновления оффера', error);
			throw error;
		}
	}

	/**
	 * Удалить офер
	 */
	static async delete(id: number): Promise<boolean> {
		const response = await OffersDAO.delete(id);

		if (response.ok) {
			Core.showNotification.success('Предложение удалёно');
		}

		return response.ok;
	}

	/**
	 * Снять офер с публикации
	 */
	static async unpublish(id: number): Promise<boolean> {
		const response = await OffersDAO.unpublish(id);

		if (response.ok) {
			Core.showNotification.success('Предложение снято с публикации');
		}

		return response.ok;
	}


	// ============================================================
	//  Бизнес-ограничения
	// ============================================================

	static reasonUnavailableEdit(offer: Offer): string | null {
		if (offer.seller.id !== Core.currentPartner.id) {
			return REASON_UNAVAILABLE.USER_HAS_NO_ACCESS;
		}
		else {
			return null;
		}
	}

	static reasonUnavailableDelete(offer: Offer): string | null {
		if (offer.seller.id !== Core.currentPartner.id) {
			return REASON_UNAVAILABLE.USER_HAS_NO_ACCESS;
		}
		else {
			return null;
		}
	}

	static reasonUnavailableTakeOff(offer: Offer): string | null {
		if (offer.seller.id !== Core.currentPartner.id) {
			return REASON_UNAVAILABLE.USER_HAS_NO_ACCESS;
		}
		else if (offer.isPublished === false) {
			return REASON_UNAVAILABLE.NOT_PUBLISHED;
		}
		else {
			return null;
		}
	}

	// ============================================================
	//  Работа с характеристиками
	// ============================================================

	static setExperimentCharacteristics(characteristics: any[]): any[] {
		if (!Array.isArray(characteristics) || characteristics.length === 0) {
			return [];
		}

		return characteristics.map(item => {
			if (item.valueType === 'multilist') {
				const index = item.allowedValues.findIndex(
					(value: any) => value.name === 'Нет',
				);
				if (index > -1) {
					item.emptyValue = { ...item.allowedValues[index] };
					item.allowedValues.splice(index, 1);
					item.emptySelected = null;
					if (Array.isArray(item.value) && item.value.length > 0) {
						item.emptySelected
							= item.value.length === 1
							&& item.value[0].id === item.emptyValue.id;
					}
				}
			}
			return item;
		});
	}

	// ============================================================
	//  Страница оферов — конфигурация UI
	// ============================================================

	static readonly DEFAULT_TAB_ID = 'all';

	static readonly TAB_GROUP_LABELS = [
		{ group: 'samples',    groupLabel: 'Образцы'},
		{ group: 'bookmarked', groupLabel: 'Избранные'},
		{ group: 'all',        groupLabel: 'Все' },
		{ group: 'active',     groupLabel: 'Мои активные'},
	] as const;

	/** поля group и groupLabel необходимы для формирования групп вкладок и пердаются в качестве аргументов
	 * в конструктор TabElementGrouped, который является наследником TabElement
	 */
	static readonly FLOW_TABS = [
		{ id: 'isSamples',    group: 'samples',      name: 'Образцы',         hidden: false,  roles: ['buyer', 'seller'] },
		{ id: 'isNotSamples', group: 'samples',      name: 'Обычные',         hidden: true,   roles: ['buyer', 'seller'] },
		{ id: 'bookmarked',   group: 'bookmarked',   name: 'Избранные',       hidden: false,  roles: ['buyer', 'seller'] },
		{ id: 'all',          group: 'all',          name: 'Все',             hidden: false,  roles: ['buyer', 'seller'] },
		{ id: 'my-active',    group: 'active',       name: 'Мои активные',    hidden: false,  roles: ['seller'] },
		{ id: 'my-inactive',  group: 'active',       name: 'Мои неактивные',  hidden: true,   roles: ['seller'] },
	] as const;

	static getTabGroupLabel(group: string): string | undefined {
		return this.TAB_GROUP_LABELS.find(item => item.group === group)?.groupLabel;
	}

	static getOffersFlowTabs(role: 'buyer' | 'seller'): TabElementGrouped[] {
		return this.FLOW_TABS
			.filter(decl => (decl.roles as readonly string[]).includes(role))
			.map(decl => TabElementGrouped.from(
				decl.id,
				decl.name,
				null,
				decl.hidden,
				decl.group,
				OffersService.getTabGroupLabel(decl.group),
			));
	}

	// ============================================================
	//  Работа с диалогами
	// ============================================================
	static showOfferCard(offerOrId: Offer | number): Promise<boolean> {
		if (offerOrId instanceof Offer) {
			return Core.dialogs.OfferCard.showAsPromise({ offer: offerOrId });
		}
		else {
			return Core.dialogs.OfferCard.showAsPromise({ id: offerOrId });
		}
	}


	static showOfferEditDialog(
		params: {
			id?:         number | null;
			offer?:      Offer  | null;
			categoryId?: number | null;
			demand?:     Demand | null;
			tender?:     Tender | null;
		} = {}
	): Promise<boolean> {
		return Core.dialogs.OfferEditDialog.showAsPromise(params);
	}

	/**
	 * Удалить предложение с подтверждением.
	 * Возвращает true если удаление произошло — вызывающий сам решает что делать
	 * (обычно refresh лоадера регистра).
	 */
	static async deleteWithConfirm(id: number): Promise<boolean> {
		const confirmed = await Core.confirm('Подтверждаете удаление предложения?');

		if (confirmed) {
			return await this.delete(id);
		} else {
			return false;
		}
	}

	/**
	 * Открывает ленту истории/комментариев
	 */
	static showFeed(offerId: number): void {
		Core.dialogs.FeedDialog.show({
			entityTypeName: 'offer',
			entityId:       offerId,
			showAddComment: false,
		});
	}

	/**
	 * Открывает диалог создания заказа
	 */
	static async showOrderCreateDialogMinimized(offer: Offer, tender: | null = null): Promise<boolean> {
		return Core.dialogs.OrderCreateDialogMinimized.showAsPromise(
			{
				offer: offer,
				tender: tender,
				showOrderType: true,
			},
		);
	}

	static get contextMenu(): ContextMenuItem[] {
		return [
			new ContextMenuItem(
				'Открыть',
				'open_in_new',
				(offer: Offer) => { return this.showOfferCard(offer.id); },
				null,
				false,  // hidden
				true,   // dividerAfter
			),
			new ContextMenuItem(
				'Редактировать',
				'edit',
				(offer: Offer) => { return this.showOfferEditDialog( { id: offer.id} ); },
				(offer: Offer) => OffersService.reasonUnavailableEdit(offer),
				() => Core.currentPartner.isBuyer === true,
			),
			new ContextMenuItem(
				'Создать заказ',
				'add_shopping_cart',
				(offer: Offer) => { return OffersService.showOrderCreateDialogMinimized(offer); },
				null,
				() => Core.currentPartner.isSeller === true,
			),
			new ContextMenuItem(
				'История действий',
				'history',
				(offer: Offer) => { this.showFeed(offer.id); },
				null,
				() => Core.currentPartner.isSeller === true,
			),
			new ContextMenuItem(
				'Снять с публикации',
				'unpublished',
				(offer: Offer) => { return OffersService.unpublish(offer.id); },
				(offer: Offer) => OffersService.reasonUnavailableTakeOff(offer),
				() => Core.currentPartner.isBuyer === true,
				true,   // dividerAfter
			),
			new ContextMenuItem(
				'Удалить',
				'delete',
				(offer: Offer) => { return this.deleteWithConfirm(offer.id);},
				(offer: Offer) => OffersService.reasonUnavailableDelete(offer),
				() => Core.currentPartner.isBuyer === true,
			),
		];
	}

	static get groupContextMenu(): ContextMenuItem[] {
		return [
			new ContextMenuItem(
				'Добавить предложение',
				'',
				/* Для OffersRegistryExtended предложения после того, как приходят плоским списком
					с бэка, группируются по категории и имеют следующую структуру
					{ id : number , offers[Offer], productCategory : {} } */
				(category: any) => { return this.showOfferEditDialog({ categoryId: category.id }); },
			),
		];
	}
}

export default OffersService;